import { difference, get } from 'lodash';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type { Subscription } from 'rxjs';

import type { FetchServiceMeta, WidgetContainer } from '@swf/core/types/Widget';

interface Subscriptions {
  [key: string]: { [destination: string]: Subscription };
}

export const useSubscriptions = (widget: WidgetContainer) => {
  const { current: subscriptions } = useRef<Subscriptions>({});
  const dispatch = useDispatch()[widget.id];

  const unsubscribe = useCallback(
    (serviceKey: string): void => {
      Object.values(subscriptions[serviceKey]).forEach((sub) => sub.unsubscribe());
      delete subscriptions[serviceKey];
    },
    [subscriptions]
  );

  const updateSubscriptions = useCallback(
    async (key: string, metas: FetchServiceMeta[] = []) => {
      if (get(widget, ['services', key, 'config', 'subscribe'])) {
        const oldSubs = Object.keys(subscriptions[key] || {});
        const newSubs = await Promise.all(metas.map((meta) => dispatch.fetch({ key, meta })));
        difference(oldSubs, newSubs).forEach((destination) => unsubscribe(destination));
      }
    },
    [dispatch, widget, subscriptions, unsubscribe]
  );

  return updateSubscriptions;
};
